import { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@contexts/auth-context';
import { forgotPassword } from '@services/authentication';
import {
  TextInput,
  PasswordInput,
  Checkbox,
  Anchor,
  Paper,
  Title,
  Text,
  Container,
  Group,
  Button,
  Loader,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import AuthLogo from '@components/auth-logo';
import AuthModal from '@components/auth-modal';
import classes from './auth-common.module.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const stateUsername = (location.state as { username?: string } | null)?.username || '';
  const queryParams = new URLSearchParams(location.search);
  const queryUsername = queryParams.get('login_name') || '';
  const initialUsername = stateUsername || queryUsername;
  const { signIn, isAuthenticated } = useContext(AuthContext);
  const [opened, { open, close }] = useDisclosure(false);
  const [username, setUsername] = useState<string>(initialUsername);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    // this context signIn() set the context if the user is authenticated, or return the newPasswordRequired
    // response if the user needs to set a new password
    signIn(username, password, rememberMe)
      .then((result) => {
        if (result && 'newPasswordRequired' in result && result.newPasswordRequired) {
          navigate('/set-new-password', {
            state: {
              username,
              userAttributes: result.userAttributes,
              requiredAttributes: result.requiredAttributes,
            },
          });
        }
      })
      .catch((err) => {
        setError((err as Error)?.message || 'An error occurred');
        setIsLoading(false);
      });
  };

  const handleForgotPassword = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsLoading(true);
    setIsForgotPassword(true);
    setError('');

    if (emailInputRef.current && !emailInputRef.current.reportValidity()) {
      setIsLoading(false);
      return;
    }

    forgotPassword(username.trim())
      .then(() => {
        open();
        setIsLoading(false);
      })
      .catch((err) => {
        setError((err as Error)?.message || 'An error occurred');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoading(false);
      navigate('/clients');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!stateUsername) return;

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate, stateUsername]);

  return (
    <Container size={420} my={40}>
      <form onSubmit={handleSubmit}>
        <AuthLogo />

        <Paper withBorder shadow="md" p={24} mt={20} radius="md">
          <Title order={3} className={classes.title} ta="center" mb={12}>
            Welcome
          </Title>
          <TextInput
            label="Email"
            placeholder="user@govoylogic.com"
            type="email"
            required
            onChange={(e) => setUsername(e.target.value)}
            disabled={isLoading}
            value={username}
            ref={emailInputRef}
          />
          <PasswordInput
            label="Password"
            placeholder="Your password"
            required={!isForgotPassword}
            mt="md"
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          <Group justify="space-between" mt="lg">
            <Checkbox
              label="Remember me"
              checked={rememberMe}
              onChange={() => setRememberMe(!rememberMe)}
              disabled={isLoading}
            />
            <Anchor
              component="button"
              size="sm"
              type="button"
              disabled={isLoading}
              tabIndex={-1}
              onClick={(event) => handleForgotPassword(event)}
            >
              Forgot password?
            </Anchor>
          </Group>
          <Button fullWidth mt="xl" type="submit" disabled={isLoading}>
            {isLoading ? <Loader size="sm" color="white" /> : 'Sign in'}
          </Button>
          {error && (
            <Text c="red" pt={20}>
              {error}
            </Text>
          )}
        </Paper>
        <Text c="dimmed" size="sm" ta="center" mt={25}>
          Have your confirmation code?&nbsp;
          <Anchor size="sm" component={Link} to="/reset-confirm" state={{ email: username }}>
            Confirm account
          </Anchor>
        </Text>
        <Text c="dimmed" size="xs" ta="center" mt={12}>
          <Anchor size="xs" component={Link} to="/privacy">
            Privacy Policy
          </Anchor>
          &nbsp;·&nbsp;
          <Anchor size="xs" component={Link} to="/terms">
            Terms of Use
          </Anchor>
        </Text>
      </form>
      <AuthModal
        opened={opened}
        close={close}
        navTo="/reset-confirm"
        state={{ username }}
        title="Forgot password"
        message="If you email is registered, you will receive a confirmation code to reset your password."
      />
    </Container>
  );
}

export default Login;
